import { useState, Fragment } from 'react';
import { useDispatch } from 'react-redux';

import reduce from 'lodash/reduce';
import AjvErrors from 'ajv-errors';
import Ajv from 'ajv';

import { CardHeader, Divider, Grid, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { partManagementActions } from '../../../store/reducers/part-management';
import { partDetailsSchema, partDetailsUISchema, partTagsSchema } from '../form';
import FormModal from '../../../common/formModal';
import MetaTagsTable from '../../../common/metaTagsTable';
import { get } from 'lodash';
import { useOutletContext } from 'react-router-dom';
import { palette } from '../../../theme/palette';

const Styles = {
  gridItem: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '10px',
  },
  gridContainer: {
    marginTop: '24px',
    marginBottom: '32px',
  },
  card: {
    margin: '0px',
    background: `${palette.primary.contrastText} !important`,
    padding: '24px',
    gap: '24px',
    height: '622px',
    boxShadow: '0px 0px 14px rgba(0, 0, 0, 0.05)',
    borderRadius: '18px',
  },
  cardHeader: {
    padding: '0px 0px 24px 0px',
  },
  title: {
    fontSize: '1rem',
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '22px',
    color: '#434D67',
  },
  inputLabel: {
    fontWeight: '400',
    fontSize: '1rem',
    lineHeight: '22px',
    color: '#434D67',
    marginRight: '25px',
  },
  input: {
    '&.MuiTextField-root': {
      margin: '0',
    },
  },
  inputGrid: {
    '&.MuiGrid-item': {
      p: '0',
    },
  },
};

const AddPart = ({
  isOpen,
  partData,
  setPartData,
  partTagsData,
  setPartTagsData,
  isEdit,
  closeModal = () => { },
  partMasterData,
  pageData
}: any) => {
  const themeData: any = useTheme();
  const dispatch = useDispatch();
  const responsiveSize = useMediaQuery('(max-width:900px)');
  const ajv = new Ajv({ allErrors: true });
  AjvErrors(ajv);
  const [ajvErrors, setAjvErrors] = useState<any>({});
  const [isValidName, setIsValidName] = useState<any>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setPartData({ ...partData, [name]: value });
    if (name === 'part_name') {
      if (partMasterData.findIndex((item: any) => item.part_name.toLowerCase() === value.toLowerCase()) > -1) {
        var obj = {
          ['part_name']: true,
        };
        setIsValidName(obj);
      } else {
        setIsValidName({});
      }
    }
    // validateForm()
  };

  const validateForm = () => {
    let ajvSchema: any = JSON.parse(JSON.stringify({ ...partDetailsSchema }));

    if (partTagsData.length) {
      ajvSchema = {
        ...partDetailsSchema,
        properties: {
          ...partDetailsSchema.properties,
          part_tags: {
            type: 'array',
            items: partTagsData.map((item: any) => partTagsSchema),
          },
        },
        required: [...partDetailsSchema.required, 'part_tags'],
      };
    }

    const validate = ajv.compile(ajvSchema);
    const reqParams = {
      ...partData,
      part_tags: partTagsData,
    };

    if (validate({ ...reqParams })) {
      setAjvErrors({});
      return true;
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
      return false;
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();

    let ajvSchema: any = JSON.parse(JSON.stringify({ ...partDetailsSchema }));

    if (partTagsData.length) {
      ajvSchema = {
        ...partDetailsSchema,
        properties: {
          ...partDetailsSchema.properties,
          part_tags: {
            type: 'array',
            items: partTagsData.map((item: any) => partTagsSchema),
          },
        },
        required: [...partDetailsSchema.required, 'part_tags'],
      };
    }

    const validate = ajv.compile(ajvSchema);

    const trimmedPartData = reduce(
      partData,
      (acc: any, value: any, key: any) => {
        acc[key] = typeof value === 'string' ? value.trim() : value;
        return {
          ...acc,
        };
      },
      {}
    );
    setPartData(trimmedPartData);

    const trimmedPartTagsData = partTagsData.map((item: any) => {
      return {
        ...item,
        key: typeof item.key === 'string' ? item.key.trim() : item.key,
        value: typeof item.value === 'string' ? item.value.trim() : item.value,
      };
    });

    setPartTagsData(trimmedPartTagsData);

    const reqParams = {
      ...trimmedPartData,
      part_tags: trimmedPartTagsData,
    };

    if (validate({ ...reqParams })) {
      if (isEdit) {
        dispatch(partManagementActions.updatePartRequest(reqParams));
      } else {
        if (
          partMasterData.findIndex((item: any) => item.part_name.toLowerCase() === partData.part_name.toLowerCase()) >
          -1
        ) {
          var obj = {
            ['part_name']: true,
          };
          setIsValidName(obj);
        } else {
          dispatch(partManagementActions.addPartRequest(reqParams));
        }
        setAjvErrors({});
      }
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      title={`${isEdit ? 'EDIT ' : 'ADD NEW '} ${get(pageData, 'entity_name', 'Part').toUpperCase()}`}
      pageData
      handleSubmit={handleSubmit}
      handleClose={closeModal}
    >
      <CardHeader title={`${get(pageData, 'entity_name', 'Details')} Details`} titleTypographyProps={Styles.title}  />
      <Divider />
      <Grid container spacing={1} textAlign={'right'} sx={Styles.gridContainer}>
        {partDetailsUISchema.map(({ label, name }: any) => (
          <Fragment key={name}>
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              sx={{ ...Styles.gridItem, justifyContent: responsiveSize ? 'flex-start' : 'flex-end', p: responsiveSize ? '0 0 4px 8px !important' : '' }}
            >
              <Typography sx={{ ...Styles.inputLabel, color: themeData.palette.text.primary }}>
                {label.replace('Part', get(pageData, 'entity_name', 'Part'))}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={8}
              sx={{
                '&.MuiGrid-item': {
                  pt: 0,
                },
              }}
            >
              <TextField
                sx={Styles.input}
                margin='normal'
                fullWidth
                id={name}
                onChange={handleChange}
                // onBlur={validateForm}
                name={name}
                autoComplete={name}
                autoFocus
                variant='outlined'
                value={partData[name]}
                size='small'
                helperText={
                  name === 'part_name' ? (
                    isValidName['part_name'] ? (
                      <p
                        style={{
                          color: '#D81010',
                          fontSize: '0.75rem',
                          textAlign: 'left',
                          padding: '0',
                          margin: '0',
                          marginLeft: '5px',
                        }}
                      >
                        {get(pageData, 'entity_name', 'Part').toUpperCase()} NAME already exists!! Please specify alternative "{get(pageData, 'entity_name', 'Part').toUpperCase()} NAME".{' '}
                      </p>
                    ) : (
                      ajvErrors.instancePath && ajvErrors.instancePath.includes(name) && ajvErrors.message
                    )
                  ) : (
                    ajvErrors.instancePath && ajvErrors.instancePath.includes(name) && ajvErrors.message
                  )
                }
                error={
                  name === 'part_name'
                    ? isValidName['part_name']
                      ? true
                      : ajvErrors.instancePath && ajvErrors.instancePath.includes(name)
                    : ajvErrors.instancePath && ajvErrors.instancePath.includes(name)
                }
              // helperText={ajvErrors.instancePath && ajvErrors.instancePath.includes(name) && ajvErrors.message}
              // error={ajvErrors.instancePath && ajvErrors.instancePath.includes(name)}
              />
            </Grid>
          </Fragment>
        ))}
      </Grid>
      <CardHeader
        title='Metadata (Supporting information for effective search)'
        titleTypographyProps={Styles.title}
      />
      <Divider/>
      <MetaTagsTable
        data={partTagsData}
        setData={setPartTagsData}
        error={ajvErrors.instancePath && ajvErrors.instancePath.includes('part_tags')}
      />
    </FormModal>
  );
};

export default AddPart;
